// src/features/developers/access.ts
//
// Who reaches «Desarrolladores» and who reaches nothing else. Two independent grants:
//  - a role of the application (any of them but Receptionist) opens the page as one more screen;
//  - a documentation realm role (`int-insight` for partners and customer IT, `doc-insight` for the
//    team) WITHOUT any application role is a «documentation-only» person: this page and nothing else.
// The API enforces the second one (it answers 403 to anything but the documentation endpoints);
// what is decided here only shapes the interface, never the security.
import type { UserRole } from '@/types/models';

export const DOCUMENTATION_REALM_ROLES = ['int-insight', 'doc-insight'];

const FLAG_KEY = 'documentation_only';

/** The page of the section; the only route a documentation-only person may visit. */
export const DEVELOPERS_ROUTE = '/developers';
export const DEVELOPERS_KEYS_ROUTE = '/developers/keys';

export const isDeveloperPath = (pathname: string): boolean =>
  pathname === DEVELOPERS_ROUTE || pathname.startsWith(`${DEVELOPERS_ROUTE}/`);

/** Persisted by the sign-in sync; read synchronously by the shell on every render. */
export const isDocumentationOnly = (): boolean => {
  try {
    return localStorage.getItem(FLAG_KEY) === '1';
  } catch {
    return false;
  }
};

export const setDocumentationOnly = (value: boolean): void => {
  try {
    if (value) localStorage.setItem(FLAG_KEY, '1');
    else localStorage.removeItem(FLAG_KEY);
  } catch {
    // Storage unavailable: the person simply is not treated as documentation-only on this device.
  }
};

interface TokenClaims {
  realm_access?: { roles?: string[] };
  resource_access?: Record<string, { roles?: string[] } | undefined>;
}

/**
 * From the token alone: a documentation realm role and no role under the application's own client.
 * Mirrors the API's rule (a person with any application role is a person, whatever else they hold).
 */
export const documentationOnlyFromToken = (claims: unknown, clientId: string): boolean => {
  const parsed = (claims ?? {}) as TokenClaims;
  const realmRoles = (parsed.realm_access?.roles ?? []).map((role) => role.toLowerCase());
  const hasDocumentationRole = DOCUMENTATION_REALM_ROLES.some((role) => realmRoles.includes(role));
  const applicationRoles = parsed.resource_access?.[clientId]?.roles ?? [];
  return hasDocumentationRole && applicationRoles.length === 0;
};

/** The section is offered to every role but Receptionist, and to documentation-only people. */
export const canSeeDevelopers = (role: UserRole | null, documentationOnly: boolean): boolean =>
  documentationOnly || (role !== null && role !== 'Receptionist');

/**
 * The route a documentation-only person is sent to from `pathname`, or `null` when they may stay.
 * Everything outside the section sends them back to it.
 */
export const documentationOnlyRedirect = (pathname: string, documentationOnly: boolean): string | null =>
  documentationOnly && !isDeveloperPath(pathname) ? DEVELOPERS_ROUTE : null;

/** Staff-only key management is not part of what a documentation-only person reads. */
export const canOpenKeys = (documentationOnly: boolean, canManageKeys: boolean | undefined): boolean =>
  !documentationOnly && canManageKeys === true;
